import type { MessageSegment } from "@/lib/utils/aiChatUtils";

/**
 * AI SDK dynamic-tool states that mean the call has finished (success or failure).
 * Matches the terminal set used by ChatToolCallDisplay.
 */
const TERMINAL_TOOL_STATES = new Set([
  "output-available",
  "output-error",
  "output-denied",
]);

/**
 * Human-friendly loading labels for each MCP tool.
 *
 * Shown in Herald chat while a tool call is in progress. Raw tool names are
 * implementation details — users see a short status line instead.
 */
const TOOL_LOADING_LABELS: Readonly<Record<string, string>> = {
  // Feed tools
  list_feeds: "Reviewing your newsfeeds…",
  compare_feeds: "Comparing media coverage…",
  get_share_of_voice: "Calculating share of voice…",
  get_feed_summary: "Summarizing feed activity…",

  // Key message tools
  list_key_messages: "Loading key topics…",
  get_key_message_articles: "Finding key topic coverage…",

  // Article tools
  get_feed_articles: "Pulling recent articles…",
  search_feed_articles: "Searching for articles…",
  get_sentiment_trend: "Analyzing sentiment trends…",
  get_top_publishers: "Ranking top publishers…",
  get_article_detail: "Fetching article details…",

  // Story tools
  list_stories: "Finding narratives…",
  get_story_detail: "Examining the story…",
  get_trending_stories: "Checking what's trending…",
  get_story_articles: "Gathering story coverage…",

  // Broadcast tools
  get_broadcast_clips_summary: "Summarizing broadcast coverage…",
  get_broadcast_clips: "Pulling broadcast clips…",
  search_broadcast_clips: "Searching broadcast clips…",
  get_broadcast_clip_detail: "Fetching clip details…",
  get_broadcast_volume_trend: "Analyzing broadcast volume…",
  get_top_broadcast_stations: "Ranking top stations…",

  // Narrative intelligence tools
  classify_narrative: "Classifying the narrative…",

  // X Intelligence tools
  analyze_x_narrative: "Analyzing the conversation on X…",
  get_top_x_posts: "Finding top posts on X…",
  get_narrative_influencers: "Identifying key voices…",
};

export const DEFAULT_LABEL = "Thinking…";

/**
 * Resolves the loading label for a tool by name.
 *
 * Unknown tools get {@link DEFAULT_LABEL} so the UI never shows raw snake_case
 * names if the server adds tools before this map is updated.
 */
export const getToolLoadingLabel = (toolName: string): string =>
  TOOL_LOADING_LABELS[toolName] ?? DEFAULT_LABEL;

/**
 * First tool in segment order that is still running (non-terminal state).
 */
export const firstRunningToolName = (
  segments: MessageSegment[],
): string | null => {
  for (const segment of segments) {
    if (segment.kind !== "tools") {
      continue;
    }
    for (const tool of segment.tools) {
      if (!TERMINAL_TOOL_STATES.has(tool.state)) {
        return tool.toolName;
      }
    }
  }
  return null;
};

interface AssistantStreamingQuipSuppressionInput {
  segments: MessageSegment[];
  hasSpec: boolean;
  spec: unknown | null;
  isLatestAssistantStreaming: boolean;
  showDevToolCalls: boolean;
}

/** True when footer streaming quips should stay hidden for this assistant turn. */
export const assistantMessageSuppressesStreamingQuips = ({
  segments,
  hasSpec,
  spec,
  isLatestAssistantStreaming,
  showDevToolCalls,
}: AssistantStreamingQuipSuppressionInput): boolean => {
  if (!isLatestAssistantStreaming) {
    return false;
  }
  if (showDevToolCalls) {
    return true;
  }

  const runningToolName = firstRunningToolName(segments);
  const specIsRenderable = hasSpec && spec != null;
  const hasVisibleContent =
    specIsRenderable || segments.some((seg) => seg.kind === "text");

  if (runningToolName !== null) return true;
  if (hasSpec && !specIsRenderable) return true;
  if (!hasVisibleContent) return true;
  return false;
};
